import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ErrorApi } from '../lib/api';
import { useFijarPermisos, usePermisos, useRoles } from '../lib/configuracion';
import { useMe, usePuede } from '../lib/auth';
import { useMigas } from '../lib/migas';
import { Contenedor } from '../componentes/Contenedor';
import { BotonVolver } from '../componentes/BotonVolver';
import { Boton } from '../componentes/Boton';
import { SelectorPermisos } from '../componentes/SelectorPermisos';
import { Escudo } from '../componentes/iconos';
import { NOMBRE_AMBITO } from './ConfigRoles';

/**
 * La ficha de UN rol, en su propia ruta.
 *
 * Trae su propio título y su vuelta al listado, así que **no va envuelta en la
 * `<Seccion>` de Configuración**: llevaría dos cabeceras y dos botones de volver.
 */
export function ConfigRolDetalle() {
  const { id } = useParams();
  const rolId = Number(id);

  const roles = useRoles();
  const permisos = usePermisos();
  const puedeEditar = usePuede('roles.administrar');
  const { usuario: yo } = useMe();
  const fijar = useFijarPermisos();

  const rol = roles.data?.find((r) => r.id === rolId) ?? null;

  const [seleccion, setSeleccion] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // La selección se siembra cuando llega el rol, y se vuelve a sembrar cuando el
  // servidor confirma un guardado: sin esto, «Descartar» compararía contra lo que
  // había hace dos ediciones.
  useEffect(() => {
    if (rol) setSeleccion(rol.permisos);
  }, [rol?.id, rol?.permisos]);

  useMigas([
    { texto: 'Configuración', a: '/configuracion' },
    { texto: 'Roles y permisos', a: '/configuracion/roles' },
    { texto: rol?.nombre ?? 'Rol' },
  ]);

  const esDeCopathy = rol?.ambito === 'copathy';

  /*
    Un permiso prohibido para este ámbito **no se ofrece**. El motor lo va a
    rechazar igual —lo hace cumplir un trigger desde la 009— y una casilla que
    siempre falla es una trampa, no una opción.
  */
  const asignables = useMemo(
    () => (permisos.data ?? []).filter((p) => esDeCopathy || !p.prohibidoParaEmpresa),
    [permisos.data, esDeCopathy],
  );

  if (roles.isPending || permisos.isPending) {
    return (
      <Contenedor>
        <p className="py-16 text-center text-marino-400">Cargando…</p>
      </Contenedor>
    );
  }

  if (!rol) {
    return (
      <Contenedor>
        <BotonVolver a="/configuracion/roles">Roles y permisos</BotonVolver>
        <p className="py-16 text-center text-red-600">No encontramos este rol.</p>
      </Contenedor>
    );
  }

  const esMiRol = yo?.rol === rol.clave;
  const hayCambios =
    JSON.stringify([...seleccion].sort()) !== JSON.stringify([...rol.permisos].sort());

  async function guardar() {
    setError(null);
    try {
      await fijar.mutateAsync({ rolId, permisos: seleccion });
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No pudimos guardar los permisos.');
    }
  }

  return (
    <Contenedor>
      <BotonVolver a="/configuracion/roles">Roles y permisos</BotonVolver>

      <header className="mb-5 mt-4 flex flex-wrap items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-marino-50 text-marino-600">
          <Escudo size={24} />
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-marino-800">{rol.nombre}</h1>
          <p className="mt-1 text-sm text-marino-400">
            Ámbito {NOMBRE_AMBITO[rol.ambito]} ·{' '}
            <strong className="font-semibold text-marino-600">
              {seleccion.length} de {asignables.length}
            </strong>{' '}
            permisos ·{' '}
            {rol.usuarios > 0
              ? `${rol.usuarios} ${rol.usuarios === 1 ? 'cuenta lo usa' : 'cuentas lo usan'}`
              : 'sin cuentas'}
          </p>
        </div>
      </header>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {/*
        Avisar ANTES, no cuando falle. Editar el propio rol es legítimo —hay un
        solo administrador en este proyecto— pero conviene saber que el efecto es
        inmediato sobre uno mismo.
      */}
      {esMiRol && puedeEditar && (
        <p className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <Escudo size={16} className="mt-0.5 shrink-0" />
          Es tu propio rol: lo que quites deja de estar disponible para ti en cuanto guardes.
        </p>
      )}

      {!esDeCopathy && (
        <p className="mb-4 rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-sm text-marino-500">
          Los permisos que abrirían datos de otros clientes —o respuestas individuales— no
          aparecen en esta lista: el motor los rechaza para un rol de ámbito{' '}
          {NOMBRE_AMBITO[rol.ambito]?.toLowerCase()}. Es lo que sostiene la confidencialidad que
          Copathy promete.
        </p>
      )}

      <SelectorPermisos
        permisos={asignables}
        seleccion={seleccion}
        editable={puedeEditar}
        alCambiar={setSeleccion}
      />

      {puedeEditar && (
        /*
          Barra pegada abajo: con 13 grupos, el botón al final del documento queda
          a dos pantallas del grupo que se acaba de tocar. Solo aparece cuando hay
          algo que guardar — si no, ocuparía sitio para no ofrecer nada.
        */
        <div
          /*
            ⚠️ `inert` y no solo `opacity-0`. Una barra invisible deja «Descartar»
            y «Guardar» **en el orden de tabulación** y en el árbol de
            accesibilidad: quien navega con teclado o con lector de pantalla se
            topa con dos botones que no existen en la pantalla. `pointer-events`
            solo detiene el ratón. Se vio midiendo el `tabIndex` en el navegador.
          */
          inert={!hayCambios}
          className={`sticky bottom-4 mt-4 flex items-center justify-end gap-3 rounded-2xl border
            border-marino-100 bg-white/95 px-4 py-3 backdrop-blur transition
            ${hayCambios ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        >
          <span className="mr-auto text-sm text-marino-500">Hay cambios sin guardar.</span>
          <Boton
            variante="secundario"
            className="px-3.5 py-2 text-sm"
            onClick={() => setSeleccion(rol.permisos)}
          >
            Descartar
          </Boton>
          <Boton className="px-4 py-2 text-sm" cargando={fijar.isPending} onClick={() => void guardar()}>
            Guardar
          </Boton>
        </div>
      )}

      {!puedeEditar && (
        <p className="mt-4 rounded-xl border border-marino-100 bg-marino-50/60 px-4 py-3 text-sm text-marino-500">
          Puedes ver los permisos de este rol, pero no cambiarlos.
        </p>
      )}
    </Contenedor>
  );
}
